import { Alert, CommonProp, generate, Loading } from '@acin/ui-core';
import React from 'react';
import { useRegTextViewer } from '../../RegTextProvider';
import { useInternalRegulationSegmentsQuery } from '../graphql/queries/internalRegulationSegments.generated';
import * as SC from './RegTextViewer.styled';

export interface RegTextViewerProps extends CommonProp {
  className?: string;
  activeSegmentId?: string;
  activeFragmentId?: string;
}

export const RegTextViewer: React.FC<RegTextViewerProps> = (props) => {
  const { className, activeSegmentId: propSegmentId, activeFragmentId: propFragmentId } = props;

  const viewerContext = useRegTextViewer();
  const activeSegmentId = propSegmentId ?? viewerContext.activeSegmentId;
  const activeFragmentId = propFragmentId ?? viewerContext.activeFragmentId;

  const { data, loading, error } = useInternalRegulationSegmentsQuery({
    variables: {
      input: {
        filters: {
          groups: activeSegmentId
            ? [
                {
                  groupKey: 'REGULATION_SEGMENT_ID',
                  filterValues: [{ value: activeSegmentId }],
                },
              ]
            : [],
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const fragmentRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const edges = data?.internalRegulationSegments?.edges ?? [];
  const activeSegment = edges.find((edge) => edge.node.id === activeSegmentId)?.node ?? edges[0]?.node;
  const fragmentGroups = activeSegment?.fragmentGroups ?? [];

  React.useEffect(() => {
    if (activeFragmentId && fragmentRefs.current[activeFragmentId]) {
      fragmentRefs.current[activeFragmentId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeFragmentId, data]);

  if (loading) {
    return (
      <SC.LoadingWrapper>
        <Loading />
      </SC.LoadingWrapper>
    );
  }

  if (error) {
    return (
      <Alert
        status="error"
        title="Server Error"
        message="There was an issue retrieving Regulatory text for this article"
      />
    );
  }

  if (!fragmentGroups.length) {
    return <Alert status="info" title="No regulatory text available." />;
  }

  return (
    <SC.RegTextViewer data-testid={generate(['reg-text-viewer'], props)} className={className}>
      <SC.List>
        {fragmentGroups.map((group) => {
          const isActive = group.id === activeFragmentId;
          return (
            <SC.Fragment
              key={group.id}
              isActive={isActive}
              data-testid={`${group.id}--${isActive ? 'active' : 'inactive'}`}
              ref={(el) => {
                fragmentRefs.current[group.id] = el;
              }}
            >
              {(group.fragments ?? []).map((fragment, index) => (
                <SC.Passage key={fragment.id}>
                  {fragment?.label && fragment.label.trim().length > 0 && (
                    <SC.Chapter data-testid={`${fragment.id}-chapter-${index}`}>{fragment.label}</SC.Chapter>
                  )}
                  <SC.Body data-testid={`${fragment.id}-passage-${index}`}>{fragment.body}</SC.Body>
                </SC.Passage>
              ))}
              {isActive ? <SC.Open /> : <SC.Closed />}
            </SC.Fragment>
          );
        })}
      </SC.List>
    </SC.RegTextViewer>
  );
};
